import { HelpCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { Button, TooltipTrigger } from "react-aria-components";

import { Label } from "@/components/field";
import { Tooltip } from "@/components/tooltip";
import { ControlledTipTapEditor } from "@/components/ui/controlled/ControlledTipTapEditor.tsx";

import { useCreateVideoRequestFormContext } from "../useCreateVideoRequestForm";

export function SingleInputForm() {
  const methods = useCreateVideoRequestFormContext();
  const briefSectionRef = useRef<HTMLDivElement>(null);

  const scrollToBriefSection = () => {
    briefSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const errors = methods.formState.errors.briefCreatorSingle;

    if (errors?.requestDescription || errors?.textsForDesign) {
      scrollToBriefSection();
    }
  }, [methods.formState.errors]);

  useEffect(() => {
    const errors = methods.formState.errors.briefCreatorSingle;

    if (
      methods.formState.submitCount > 0 &&
      (errors?.requestDescriptionHTML || errors?.textsForDesignHTML)
    ) {
      scrollToBriefSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.formState.submitCount]);

  return (
    <>
      <div ref={briefSectionRef} />
      <div className="h-[530px] mb-5">
        <div className="mb-xs flex items-center justify-between">
          <Label requiredHint>📄 Request description</Label>
          <TooltipTrigger delay={0}>
            <Button
              aria-label="Show tooltip"
              className="cursor-default outline-hidden"
            >
              <HelpCircle className="size-xl text-fg-quinary transition-all duration-300 hover:text-fg-quinary" />
            </Button>
            <Tooltip offset={0}>
              Describe the overall purpose and specific requirements of your
              design request. Include any critical project details that will
              guide the creation process.
            </Tooltip>
          </TooltipTrigger>
        </div>

        <ControlledTipTapEditor
          className="h-[460px]"
          control={methods.control}
          name="briefCreatorSingle.requestDescriptionHTML"
          onChangeText={(text) => {
            methods.setValue("briefCreatorSingle.requestDescription", text);
          }}
          placeholder="Tell us about your request. The more specific you are, the better the end result(s) will be. "
        />
        {methods.formState.errors.briefCreatorSingle?.requestDescription &&
        methods.watch("briefCreatorSingle.requestDescription").length === 0 ? (
          <span
            className="block text-sm font-normal text-text-error-primary mt-sm"
            slot="errorMessage"
          >
            Request description is required
          </span>
        ) : null}
      </div>

      <div className="h-[530px]">
        <div className="mb-xs flex items-center justify-between">
          <Label requiredHint>💬 Text for design</Label>
          <TooltipTrigger delay={0}>
            <Button
              aria-label="Show tooltip"
              className="cursor-default outline-hidden"
            >
              <HelpCircle className="size-xl text-fg-quinary transition-all duration-300 hover:text-fg-quinary" />
            </Button>
            <Tooltip offset={0}>
              Enter the exact text, key messages, and calls to action that
              should be featured on the design. Specify any slogans or phrases
              to ensure they align with your communication goals.
            </Tooltip>
          </TooltipTrigger>
        </div>

        <ControlledTipTapEditor
          className="h-[460px]"
          control={methods.control}
          name="briefCreatorSingle.textsForDesignHTML"
          onChangeText={(text) => {
            methods.setValue("briefCreatorSingle.textsForDesign", text);
          }}
          placeholder="Tell us what text should be displayed on your design. Please make sure to check for spelling or typos before submitting the request to avoid delays"
        />
        {methods.formState.errors.briefCreatorSingle?.textsForDesign &&
        methods.watch("briefCreatorSingle.textsForDesign").length === 0 ? (
          <span
            className="block text-sm font-normal text-text-error-primary mt-sm"
            slot="errorMessage"
          >
            Text for design is required
          </span>
        ) : null}
      </div>
    </>
  );
}
